import React, { useCallback, useEffect, useRef, useState } from "react";
import { createGame, tick, setAngle, setAbsoluteAngle, shoot, getAIMove, movePlayerVertical } from "./engine.js";
import type { DodgeballGame } from "./types.js";
import { CANVAS_W, CANVAS_H, WALL_TOP, WALL_BOTTOM, PLAYER_RADIUS, MAX_HEALTH } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

export default function DodgeballPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [game, setGame] = useState<DodgeballGame>(() => createGame());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const p1Keys = useRef({ up: false, down: false });
  const p2Keys = useRef({ up: false, down: false, shoot: false });
  const shootConsumed = useRef({ p2: false });
  const mouseDownRef = useRef(false);
  const opponentInputRef = useRef({ angle: 180, wantsUp: false, wantsDown: false, shoot: false });
  const lastOppShoot = useRef(false);

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;
  gameRef.current = game;

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/dodgeball/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame());
  }

  function drawPlayer(ctx: CanvasRenderingContext2D, p: DodgeballGame["p1"], isLeft: boolean) {
    const color = isLeft ? "#3498db" : "#e74c3c";
    const dotX = isLeft ? 20 : CANVAS_W - 20;
    for (let i = 0; i < MAX_HEALTH; i++) {
      ctx.fillStyle = i < p.health ? color : "#333";
      const row = Math.floor(i / 5);
      const col = i % 5;
      ctx.beginPath();
      ctx.arc(dotX + (col - 2) * 10, WALL_TOP + 10 + row * 12, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, PLAYER_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, PLAYER_RADIUS - 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(p.x - 4, p.y - 4, 3, 0, Math.PI * 2);
    ctx.fill();
    const angleRad = (p.angle * Math.PI) / 180;
    ctx.strokeStyle = "#ffd700";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + Math.cos(angleRad) * 24, p.y + Math.sin(angleRad) * 24);
    ctx.stroke();
  }

  function render(ctx: CanvasRenderingContext2D, g: DodgeballGame) {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = "#0a1628";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = "rgba(0, 180, 255, 0.15)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, WALL_TOP);
    ctx.lineTo(CANVAS_W, WALL_TOP);
    ctx.moveTo(0, WALL_BOTTOM);
    ctx.lineTo(CANVAS_W, WALL_BOTTOM);
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 8]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_W / 2, WALL_TOP);
    ctx.lineTo(CANVAS_W / 2, WALL_BOTTOM);
    ctx.stroke();
    ctx.setLineDash([]);
    for (const b of g.balls) {
      ctx.fillStyle = "#ffd700";
      ctx.shadowBlur = 12;
      ctx.shadowColor = "#ffd700";
      ctx.beginPath(); ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
    }
    for (const b of g.frozenBalls) {
      ctx.fillStyle = "rgba(255, 200, 50, 0.3)";
      ctx.beginPath(); ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2); ctx.fill();
    }
    for (const e of g.explosions) {
      const t = e.timer / e.maxTimer;
      const r = 30 + (1 - t) * 50;
      const alpha = t;
      ctx.shadowBlur = 40;
      ctx.shadowColor = `rgba(255, 150, 30, ${alpha})`;
      ctx.fillStyle = `rgba(255, 200, 50, ${alpha * 0.8})`;
      ctx.beginPath(); ctx.arc(e.x, e.y, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(255, 80, 20, ${alpha * 0.9})`;
      ctx.beginPath(); ctx.arc(e.x, e.y, r * 0.5, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 200, ${alpha * 0.6})`;
      ctx.beginPath(); ctx.arc(e.x, e.y, r * 0.2, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
    }
    drawPlayer(ctx, g.p1, true);
    drawPlayer(ctx, g.p2, false);
    ctx.fillStyle = "#8ab4d0";
    ctx.font = "14px monospace";
    ctx.textAlign = "center";
    ctx.fillText("P1", CANVAS_W * 0.25, 14);
    ctx.fillText("P2", CANVAS_W * 0.75, 14);
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;
    render(ctx, game);
  }, [game]);

  useEffect(() => {
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      if (dt < 200) setGame((prev) => tick(prev, dt));
      const g = gameRef.current;
      if (!g.winner) {
        const pk1 = p1Keys.current;
        if (pk1.up) setGame((prev) => movePlayerVertical(prev, 1, -1));
        if (pk1.down) setGame((prev) => movePlayerVertical(prev, 1, 1));
        if (mouseDownRef.current && g.p1.cooldown === 0) {
          setGame((prev) => shoot(prev, 1));
          mouseDownRef.current = false;
        }
        if (mode === "local") {
          const pk2 = p2Keys.current;
          if (pk2.up) setGame((prev) => setAngle(prev, 2, -2));
          if (pk2.down) setGame((prev) => setAngle(prev, 2, 2));
          if (pk2.shoot && !shootConsumed.current.p2) {
            shootConsumed.current.p2 = true;
            setGame((prev) => shoot(prev, 2));
          }
        }
        if (mode === "multiplayer") {
          const opp = opponentInputRef.current;
          if (opp.wantsUp) setGame((prev) => movePlayerVertical(prev, 2, -1));
          if (opp.wantsDown) setGame((prev) => movePlayerVertical(prev, 2, 1));
          setGame((prev) => setAbsoluteAngle(prev, 2, opp.angle));
          if (opp.shoot && !lastOppShoot.current) {
            setGame((prev) => shoot(prev, 2));
          }
          lastOppShoot.current = opp.shoot;
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [mode]);

  useEffect(() => {
    if (mode !== "cpu" || game.winner) return;
    const timer = window.setTimeout(() => {
      setGame((prev) => {
        if (prev.winner) return prev;
        const move = getAIMove(prev);
        let g = move.wantsUp ? movePlayerVertical(prev, 2, -1) : prev;
        if (move.wantsDown) g = movePlayerVertical(g, 2, 1);
        g = setAngle(g, 2, move.angleDelta);
        return move.shoot ? shoot(g, 2) : g;
      });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [mode, game.p2.angle, game.p2.cooldown, game.winner, game.balls.length]);

  // ---- input relay for multiplayer ----------------------------------------

  useEffect(() => {
    if (mode !== "multiplayer" || !mpConnected) return;
    const interval = setInterval(async () => {
      const g = gameRef.current;
      if (g.winner) return;
      const res = await apiPost("/api/dodgeball/input", {
        code: roomCode, playerId: mpPlayerId, token: mpToken,
        angle: g.p1.angle,
        wantsUp: p1Keys.current.up,
        wantsDown: p1Keys.current.down,
        shoot: mouseDownRef.current,
      });
      if (res.opponentInput) opponentInputRef.current = res.opponentInput;
    }, 80);
    return () => clearInterval(interval);
  }, [mode, mpConnected, roomCode, mpPlayerId, mpToken]);

  // ---- mouse / keyboard ---------------------------------------------------

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || gameRef.current.winner) return;
    const mx = (e.clientX - rect.left) * (CANVAS_W / rect.width);
    const my = (e.clientY - rect.top) * (CANVAS_H / rect.height);
    const p1 = gameRef.current.p1;
    const dx = mx - p1.x;
    const dy = my - p1.y;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    setGame((prev) => setAbsoluteAngle(prev, 1, angle));
  }

  function handleMouseDown() {
    if (gameRef.current.winner) return;
    mouseDownRef.current = true;
  }

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "w" || e.key === "W") p1Keys.current.up = true;
      if (e.key === "s" || e.key === "S") p1Keys.current.down = true;
      if (e.key === "ArrowUp") p2Keys.current.up = true;
      if (e.key === "ArrowDown") p2Keys.current.down = true;
      if (e.key === "ArrowLeft" || e.key === "Enter") { e.preventDefault(); p2Keys.current.shoot = true; }
    }
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === "w" || e.key === "W") p1Keys.current.up = false;
      if (e.key === "s" || e.key === "S") p1Keys.current.down = false;
      if (e.key === "ArrowUp") p2Keys.current.up = false;
      if (e.key === "ArrowDown") p2Keys.current.down = false;
      if (e.key === "ArrowLeft" || e.key === "Enter") { p2Keys.current.shoot = false; shootConsumed.current.p2 = false; }
    }
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKeyUp);
    return () => { window.removeEventListener("keydown", handleKey); window.removeEventListener("keyup", handleKeyUp); };
  }, []);

  // ---- API / server event helpers -----------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return await res.json();
    } catch { return { type: "error", message: "Network error." }; }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`);
        break;
      case "game-reset":
        setGame(createGame());
        opponentInputRef.current = { angle: mpPlayerId === 1 ? 180 : 0, wantsUp: false, wantsDown: false, shoot: false };
        break;
      case "opponent-left":
        setMpConnected(false);
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMessage("Opponent left the game.");
        setGame(createGame());
        break;
      case "error":
        setMpError(event.message);
        break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) { handleServerEvent(event); if (event.type === "opponent-left") { pollingRef.current = false; return; } }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
  }

  useEffect(() => { return () => stopPolling(); }, []);

  // ---- multiplayer room actions -------------------------------------------

  const [message, setMessage] = useState("");

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1Name.trim() || "Player 1", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMessage(`Room created! Share this code: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p1Name.trim() || "Player 2", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setP1Color(result.opponentColor);
    setMpConnected(true);
    setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setGame(createGame());
    setMessage("Left the game.");
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setGame(createGame());
    setMessage("");
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner === 1 ? "Player 1" : game.winner === 2 ? "Player 2" : undefined} />
      {!hideTitle && <h1 className="title">Dodgeball</h1>}

      <section className="control-panel">
        <p className="status">{game.winner ? `${game.winner === 1 ? "Player 1" : "Player 2"} wins!` : game.message}</p>
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
            <option value="cpu">vs Computer</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
        </div>

        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col">
            <label>Your Name <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
            <label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}</select></label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col"><label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} /></label></div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}

        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar"><span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span></div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        {message && mode !== "multiplayer" && <p className="subtle">{message}</p>}
      </section>

      <section className="board-panel">
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="dodgeball-canvas"
          onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} />
        <div className="how-to-play">
          <p className="how-title">Controls</p>
          {mode === "local" ? (
            <><p>P1: W/S move · Mouse aim · Click to shoot</p><p>P2: ↑/↓ aim · ← or Enter shoot</p></>
          ) : (
            <p>W/S move · Mouse aim · Click to shoot</p>
          )}
          <p>Balls bounce off top & bottom. Last one standing wins!</p>
        </div>
      </section>
    </div>
  );
}
